import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, FileText, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { TypeFinancementPicker } from '../financement/TypeFinancementPicker';

const LIGNE_VIDE = { nom: '', montant: '', adresse: '', code_postal: '', ville: '', type_financement: '' };

const trouverSite = (data, centre) => {
  const cle = (centre || 'atlantique').toLowerCase();
  if (data?.sites) return data.sites.find(s => (s.site_id || '').toLowerCase() === cle || (s.site_name || '').toLowerCase() === cle);
  return data?.[cle.charAt(0).toUpperCase() + cle.slice(1)];
};

// Financeurs et structure déjà présents sur le devis (pour la régénération)
const lignesDuDevis = (devis) => {
  const f = devis?.facturation || {};
  const vers = (type, x) => ({ ...LIGNE_VIDE, ...x, type, montant: x.montant || '' });
  const financeurs = f.financeurs?.length ? f.financeurs : (f.financeur?.actif && f.financeur?.montant > 0 ? [f.financeur] : []);
  const lignes = financeurs.map(x => vers('financeur', x));
  if (f.entreprise?.actif && f.entreprise?.montant > 0) lignes.push(vers('entreprise', f.entreprise));
  return lignes;
};

const BoutonTaux = ({ label, valeur, actif, onClick, testId }) => (
  <Button type="button" size="sm" variant={actif ? 'default' : 'outline'} disabled={!valeur} onClick={onClick} className="h-7 text-xs" data-testid={testId}>
    {label} {valeur ? `(${valeur} €)` : '(non renseigné)'}
  </Button>
);

const LigneCofinanceur = ({ ligne, index, onChange, onRemove }) => {
  const maj = (champ) => (e) => onChange({ ...ligne, [champ]: e.target.value });
  const structure = ligne.type === 'entreprise';
  return (
    <div className="p-3 rounded-lg bg-slate-50 border space-y-2" data-testid={`cofinanceur-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{structure ? 'Structure' : `Financeur ${ligne.type_financement ? `(${ligne.type_financement})` : ''}`}</span>
        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={onRemove} data-testid={`btn-retirer-cofinanceur-${index}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {!structure && <TypeFinancementPicker value={ligne.type_financement} onChange={t => onChange({ ...ligne, type_financement: t })} testId={`type-financement-${index}`} />}
      <div className="grid grid-cols-3 gap-2">
        <Input className="col-span-2" placeholder={structure ? 'Nom de la structure' : 'Nom du financeur (ex : Mon Compte Formation)'} value={ligne.nom} onChange={maj('nom')} data-testid={`input-cof-nom-${index}`} />
        <Input type="number" step="0.01" placeholder="Montant €" value={ligne.montant} onChange={maj('montant')} data-testid={`input-cof-montant-${index}`} />
        <Input className="col-span-3" placeholder="Adresse" value={ligne.adresse} onChange={maj('adresse')} data-testid={`input-cof-adresse-${index}`} />
        <Input placeholder="Code postal" value={ligne.code_postal} onChange={maj('code_postal')} data-testid={`input-cof-cp-${index}`} />
        <Input className="col-span-2" placeholder="Ville" value={ligne.ville} onChange={maj('ville')} data-testid={`input-cof-ville-${index}`} />
      </div>
    </div>
  );
};

export const DevisPifDialog = ({ open, onOpenChange, centre, devis, onGenerate, loading }) => {
  const [tauxProfil, setTauxProfil] = useState({ formation: 0, apprentissage: 0 });
  const [taux, setTaux] = useState('');
  const [lignes, setLignes] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get('/centre/profiles').then(res => {
      const site = trouverSite(res.data, centre) || {};
      const t = { formation: parseFloat(site.taux_horaire_formation) || 0, apprentissage: parseFloat(site.taux_horaire_apprentissage) || 0 };
      setTauxProfil(t);
      setTaux(devis?.taux_horaire || t.formation || '');
    }).catch(() => {});
    setLignes(lignesDuDevis(devis));
  }, [open, centre, devis]);

  const ajouter = (type) => setLignes([...lignes, { ...LIGNE_VIDE, type }]);
  const totalCof = lignes.reduce((s, l) => s + (parseFloat(l.montant) || 0), 0);
  const valide = parseFloat(taux) > 0 && lignes.every(l => l.nom.trim() && parseFloat(l.montant) > 0);
  const generer = () => onGenerate({
    taux_horaire: parseFloat(taux),
    cofinanceurs: lignes.map(l => ({ ...l, nom: l.nom.trim(), montant: parseFloat(l.montant) })),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="devis-pif-dialog">
        <DialogHeader>
          <DialogTitle>Générer le devis depuis le PIF</DialogTitle>
          <DialogDescription>Les heures viennent du PIF. Choisissez le taux et qui prend en charge la formation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="devis-pif-taux">Taux horaire (€/h)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Input id="devis-pif-taux" type="number" step="0.01" value={taux} onChange={e => setTaux(e.target.value)} className="w-28 h-9" data-testid="input-devis-pif-taux" />
              <BoutonTaux label="Formation" valeur={tauxProfil.formation} actif={parseFloat(taux) === tauxProfil.formation} onClick={() => setTaux(tauxProfil.formation)} testId="btn-devis-pif-taux-formation" />
              <BoutonTaux label="Apprentissage" valeur={tauxProfil.apprentissage} actif={parseFloat(taux) === tauxProfil.apprentissage} onClick={() => setTaux(tauxProfil.apprentissage)} testId="btn-devis-pif-taux-apprentissage" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Prise en charge</Label>
            {lignes.length === 0 && <p className="text-sm text-slate-500" data-testid="devis-pif-stagiaire-seul">Stagiaire seul : il paie la totalité.</p>}
            {lignes.map((l, i) => (
              <LigneCofinanceur key={i} index={i} ligne={l}
                onChange={nv => setLignes(lignes.map((x, j) => (j === i ? nv : x)))}
                onRemove={() => setLignes(lignes.filter((_, j) => j !== i))} />
            ))}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => ajouter('financeur')} data-testid="btn-ajouter-financeur">
                <Plus className="h-4 w-4 mr-1" /> Ajouter un financeur
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={lignes.some(l => l.type === 'entreprise')} onClick={() => ajouter('entreprise')} data-testid="btn-ajouter-structure">
                <Plus className="h-4 w-4 mr-1" /> Ajouter la structure
              </Button>
            </div>
            {lignes.length > 0 && (
              <p className="text-xs text-slate-500" data-testid="devis-pif-total-cofinanceurs">
                Pris en charge : {totalCof.toLocaleString('fr-FR')} €. Le reste à charge du stagiaire est calculé automatiquement.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-devis-pif-annuler">Annuler</Button>
          <Button onClick={generer} disabled={!valide || loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-devis-pif-generer">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            Générer le devis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
